import React from "react";
import { Col, Container, Nav, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import styled from "styled-components";
import { Icon, IconButton } from '@blueprint/ui';

import {
    currentRolesSelector,
    currentUserSelector,
} from "../../redux/slices/auth/selectors";
import { superAdminAccountType } from "../../types/superadmin";
import {
    isUserAdmin,
    isUserFinanceDepartment,
    isUserHSBCOperator,
    isUserSuperAdmin,
    isNodeDeveloper,
} from "../../utils/role";
import { useLogout } from "../hooks";

import { ContentContainer } from "./core";
import { LogoIconDarkText } from "./core/icons";

type BusinessContainerProps = {
    children: React.ReactNode;
};

const BusinessContainer = ({
    children,
}: BusinessContainerProps): JSX.Element => {
    const currentRoles = useSelector(currentRolesSelector);
    const currentUser = useSelector(currentUserSelector);
    const accountType = superAdminAccountType(currentRoles);
    const isDeveloper = isNodeDeveloper();

    const logout = useLogout();

    const navItems = () => {
        if (isUserAdmin(currentRoles) || isUserSuperAdmin(currentRoles)) {
            return (
                <>
                    {currentUser?.isProfilemanagementEnabled && (
                        <NavLink
                            to="/profile-management"
                            className="nav-link px-0 mb-2 d-flex"
                            activeClassName="active"
                        >
                            {/*<EntityManagementIcon className="me-2 flex-shrink-0" />*/}
                            <Icon margin="0" name={'Settings'} className="me-2 flex-shrink-0" />
                            Profile Management
                        </NavLink>
                    )}
                    {currentUser?.isUsermanagementEnabled && (
                        <NavLink
                            to="/user-management"
                            className="nav-link px-0 mb-2 d-flex"
                            activeClassName="active"
                        >
                            {/*<UserManagementIcon className="me-2 flex-shrink-0" />*/}
                            <Icon margin="0" name={'UserMultiple'} className="me-2 flex-shrink-0" />
                            User Management
                        </NavLink>
                    )}
                    {isUserSuperAdmin(currentRoles) && (
                        <NavLink
                            to="/account-setup"
                            className="nav-link px-0 mb-2 d-flex"
                            activeClassName="active"
                        >
                            {/*<AccountSetupIcon className="me-2 flex-shrink-0" />*/}
                            <Icon margin="0" name={'PaymentsAndTransfers'} className="me-2 flex-shrink-0" />
                            {`${accountType} Account Setup`}
                        </NavLink>
                    )}
                    {isUserSuperAdmin(currentRoles) && isDeveloper && (
                        <NavLink
                            to="/relationship"
                            className="nav-link px-0 mb-2 d-flex"
                            activeClassName="active"
                        >
                            <Icon margin="0" name={'Script'} className="me-2 flex-shrink-0" />
                            Developer Entitlements
                        </NavLink>
                    )}
                    <NavLink
                        to="/user-activity-log"
                        className="nav-link px-0 mb-2 d-flex"
                        activeClassName="active"
                    >
                        {/*<UserActivityLogIcon className="me-2 flex-shrink-0" />*/}
                        <Icon margin="0" name={'DocumentEStatement'} className="me-2 flex-shrink-0" />
                        User Activity Log
                    </NavLink>
                </>
            );
        } else {
            if (isUserHSBCOperator(currentRoles)) {
                return (
                    <NavLink
                        to="/invoices"
                        className="nav-link px-0 mb-2 d-flex"
                        activeClassName="active"
                    >
                        {/*<NewFolderIcon className="me-2 flex-shrink-0" />*/}
                        <Icon margin="0" name={'MyECoupons'} className="me-2 flex-shrink-0" />
                        Invoices
                    </NavLink>
                );
            } else {
                return (
                    <>
                        <NavLink
                            to="/project"
                            className="nav-link px-0 mb-2 d-flex"
                            activeClassName="active"
                        >
                            {/*<NewFolderIcon className="me-2 flex-shrink-0" />*/}
                            <Icon margin="0" name={'Portfolio'} className="me-2 flex-shrink-0" />
                            Projects
                        </NavLink>
                        {isUserFinanceDepartment(currentRoles) && (
                            <NavLink
                                to="/collection"
                                className="nav-link px-0 d-flex"
                                activeClassName="active"
                            >
                                {/*<ContactIcon className="me-2 flex-shrink-0" />*/}
                                <Icon margin="0" name={'Survey'} className="me-2 flex-shrink-0" />
                                Collection
                            </NavLink>
                        )}
                    </>
                );
            }
        }
    };

    return (
        <Container fluid className="px-0">
            <Row className="gx-0 flex-nowrap">
                <MenuCol>
                    <nav>
                        <StyledNav
                            defaultActiveKey="#color"
                            className="vh-100 flex-column sticky-top flex-nowrap"
                        >
                            <div className="flex-grow-1">
                                <StyledLogoIcon className="d-flex" />
                                {navItems()}
                            </div>
                            <div className="nav-link mb-4 d-flex flex-column align-items-center">
                                <IconButton
                                    onClick={() => logout()}
                                    icon={'ShareIos'}
                                    className="icon-rotate-90 d-flex"
                                    variant="light.tertiary"
                                >Logout</IconButton>
                                <NavLink
                                    style={{ alignItems: 'anchor-center', whiteSpace: 'nowrap' }}
                                    className="nav-link d-flex"
                                    activeClassName="active"
                                    to="/platform-agreement"
                                    target="_blank"
                                >
                                    Platform Agreement
                                    <Icon name={'Export'} margin={2} />
                                </NavLink>
                            </div>
                        </StyledNav>
                    </nav>
                </MenuCol>
                <Col>
                    <ContentContainer className="position-relative">
                        <main>
                            {children}
                        </main>
                    </ContentContainer>
                </Col>
            </Row>
        </Container>
    );
};

const MenuCol = styled(Col)`
    max-width: 240px;
    background-color: #fff;
`;

const StyledNav = styled(Nav)`
    padding: 0 24px;

    .nav-link:not(.active):not(:hover) {
        color: #666;
    }

`;

const StyledLogoIcon = styled(LogoIconDarkText)`
    margin-top: 72px;
    margin-bottom: 20px;
    margin-left: auto;
    margin-right: auto;
    transform: translate(-16%, -24%);
`;

export default BusinessContainer;
